// ===== REACT =====
import * as React from "react";

// ===== ROUTER =====
import { useNavigate, useParams, useLocation } from "react-router-dom";

// ===== I18N =====
import { useTranslation } from "react-i18next";

// ===== UI (MUI) =====
import { Box, Paper, Typography, Divider, Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// ===== MUI ICONS =====
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ===== SERVICES =====
import { fetchNfeItem } from "../../services/nfeItemService";
import { fetchPurchaseOrders } from "../../services/purchaseOrderService";
import { simulateInvoice } from "../../services/simulateService";
import { saveAssignment } from "../../services/assignmentSaveService";
import { continueProcess } from "../../services/processContinueService";

// ===== MOCKS =====
import { getUomOptionsByMaterialVendor } from "../../mocks/uomByMaterialVendorMock";

// ===== COMPONENTS =====
import AssignmentHeaderInfo from "../../components/assignment/AssignmentHeaderInfo";
import XmlItemsGrid from "../../components/assignment/XmlItemsGrid";
import PurchaseOrderSearch from "../../components/assignment/PurchaseOrderSearch";
import PurchaseOrderGrid from "../../components/assignment/PurchaseOrderGrid";
import MessagesPanel from "../../components/assignment/MessagesPanel";
import UnitConversionDialog from "../../components/assignment/UnitConversionDialog";

const AssignmentPage = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { accessKey } = useParams();
  const location = useLocation();

  const selected = location.state?.selected;

  React.useEffect(() => {
    if (!selected) {
      navigate("/inbound", { replace: true });
    }
  }, [selected, navigate]);

  const [headerInfo, setHeaderInfo] = React.useState(selected ?? null);
  const [xmlItems, setXmlItems] = React.useState([]);

  React.useEffect(() => {
    if (!accessKey) return;

    async function loadXmlItems() {
      try {
        const items = await fetchNfeItem(accessKey);

        const mappedItems = (items || []).map((item, index) => ({
          id:
            item.id ??
            `${accessKey}-${item.item_nfe ?? item.item ?? "sem-item"}-${index}`,

          item_nf: item.item_nf ?? item.item ?? "",
          material_xml:
            item.material_xml ?? item.material_code ?? item.material_nfe ?? "",
          description_material_xml:
            item.description_material_xml ??
            item.material_description ??
            item.short_text ??
            "",
          qty: item.qty ?? item.quantity ?? 0,
          uom_xml: item.uom_xml ?? "",
          ncm_xml: item.ncm_xml ?? "",
          purchase_order: item.purchase_order ?? "",
          order_item: item.order_item ?? "",
          tax_code: item.tax_code ?? "",
          cfop: item.cfop ?? "",
          uom_erp: item.uom_sap ?? "",
          uomOptions: getUomOptionsByMaterialVendor(
            item.material_xml ?? item.material_code ?? item.material_nfe ?? "",
            selected?.supplier_cnpj ?? "",
          ),
        }));

        setXmlItems(mappedItems);

        const allAssigned = mappedItems.length > 0 &&
          mappedItems.every((i) => i.purchase_order && i.order_item);

        if (!allAssigned && headerInfo?.supplier_cnpj && headerInfo?.recipient_cnpj) {
          loadAllPo();
        }
      } catch (error) {
        console.error("erro loadXmlItems:", error);
        addMessage("E", error.message);
      }
    }
    loadXmlItems();
  }, [accessKey]);

  React.useEffect(() => {}, [xmlItems]);

  const [allPoItems, setAllPoItems] = React.useState([]);
  const [poItems, setPoItems] = React.useState([]);
  const [assignedItems, setAssignedItems] = React.useState([]);
  const [messages, setMessages] = React.useState([]);

  const [selectedXmlId, setSelectedXmlId] = React.useState(null);
  const [selectedPoId, setSelectedPoId] = React.useState(null);
  const [selectedAssignedId, setSelectedAssignedId] = React.useState(null);

  const [poSearch, setPoSearch] = React.useState({
    poNumber: "",
    poItem: "",
  });

  const [openUnitDialog, setOpenUnitDialog] = React.useState(false);
  const [loadingPo, setLoadingPo] = React.useState(false);
  const [isSimulating, setIsSimulating] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSavingOnly, setIsSavingOnly] = React.useState(false);
  const [isReadOnly, setIsReadOnly] = React.useState(false);

  function addMessage(type, text, msgIdCode = "", msgNumber = "") {
    setMessages((prev) => [
      {
        id: `${Date.now()}-${Math.random()}`,
        type,
        text,
        msgIdCode,
        msgNumber,
      },
      ...prev,
    ]);
  }

  async function loadAllPo() {
    setLoadingPo(true);
    setSelectedPoId(null);

    try {
      const result = await fetchPurchaseOrders({
        cnpjEmitente: headerInfo.supplier_cnpj,
        cnpjRemetente: headerInfo.recipient_cnpj,
        pedido: "",
        item: "",
      });

      setAllPoItems(result);
      setPoItems(result);

      if (result.length === 0) {
        addMessage("E", t("message.msg0002"));
      }
    } catch (error) {
      addMessage("E", t("message.erroBuscaPedido", { detalhe: error.message }));
    } finally {
      setLoadingPo(false);
    }
  }


  async function handleSearchPo() {
    if (loadingPo) return;

    const { poNumber, poItem } = poSearch;

    setLoadingPo(true);
    setSelectedPoId(null);

    try {
      const result = await fetchPurchaseOrders({
        cnpjEmitente:  headerInfo.supplier_cnpj,
        cnpjRemetente: headerInfo.recipient_cnpj,
        pedido:        poNumber?.trim() ?? "",
        item:          poItem?.trim()   ?? "",
      });

      setAllPoItems(result);
      setPoItems(result);

      if (result.length === 0) {
        addMessage("E", t("message.msg0002"));
      }
    } catch (error) {
      addMessage("E", t("message.erroBuscaPedido", { detalhe: error.message }));
    } finally {
      setLoadingPo(false);
    }
  }

  function handleAssign() {
    if (!selectedXmlId || !selectedPoId) {
      addMessage("E", t("message.msg0004"));
      return;
    }

    const xmlRow = xmlItems.find((x) => String(x.id) === String(selectedXmlId));
    const poRow = poItems.find((x) => String(x.id) === String(selectedPoId));

    if (!xmlRow || !poRow) {
      addMessage("E", t("message.msg0005"));
      return;
    }

    const xmlAlreadyAssigned = assignedItems.some(
      (a) => String(a.xmlItemId) === String(xmlRow.id),
    );

    if (xmlAlreadyAssigned) {
      addMessage(
        "E",
        t("message.msg0013", {
          itemNfe: xmlRow.itemNfe,
        }),
      );

      return;
    }

    const newAssignment = {
      id: `${xmlRow.id}-${poRow.id}`,
      xmlItemId: xmlRow.id,
      poItemId: poRow.id,
      category: xmlRow.category,
      itemNfe: xmlRow.itemNfe,
      qtyNfe: xmlRow.qty,
      uomNfe: xmlRow.uom,
      materialNfe: xmlRow.materialNfe,
      shortTextNfe: xmlRow.shortText,
      poNumber: poRow.poNumber,
      poItem: poRow.poItem,
      erpUom: poRow.uom,
      erpMaterial: poRow.erpMaterial,
      shortTextErp: poRow.shortText,
      iva: poRow.iva,
      status: "OK",
      message: t("message.msg0006"),
    };

    setAssignedItems((prev) => [...prev, newAssignment]);

    const uomOptions = (poRow.unidades_validas ?? []).map((u) => ({ value: u, label: u }));
    const uom_erp = uomOptions.length === 1 ? uomOptions[0].value : (poRow.uom ?? "");

    setXmlItems((prev) =>
      prev.map((item) =>
        String(item.id) === String(xmlRow.id)
          ? {
              ...item,
              purchase_order:           poRow.poNumber      ?? "",
              order_item:               poRow.poItem        ?? "",
              tax_code:                 poRow.iva           ?? "",
              material_sap:             poRow.erpMaterial   ?? "",
              description_material_sap: poRow.shortText     ?? "",
              uom_sap:                  poRow.uom           ?? "",
              ncm_sap:                  poRow.ncm           ?? "",
              deposito:                 poRow.deposito      ?? "",
              adm_lote:                 poRow.adm_lote      ?? "",
              lote:                     poRow.lote          ?? "",
              assigned: true,
              uomOptions,
              uom_erp,
            }
          : item,
      ),
    );

    setSelectedAssignedId(newAssignment.id);

    addMessage(
      "S",
      t("message.msg0009", {
        item: xmlRow.itemNfe,
        po: poRow.poNumber,
        poItem: poRow.poItem,
      }),
    );
  }

  function handleUnassign() {
    if (!selectedXmlId) {
      addMessage("E", t("message.msg0010"));
      return;
    }

    const xmlRow = xmlItems.find((x) => String(x.id) === String(selectedXmlId));

    if (!xmlRow) {
      addMessage("E", t("message.msg0007"));
      return;
    }

    // Se o item tinha pedido atribuído, devolve o PO ao grid caso não esteja lá
    if (xmlRow.purchase_order && xmlRow.order_item) {
      const jaExiste = poItems.some(
        (p) => String(p.poNumber) === String(xmlRow.purchase_order) &&
               String(p.poItem)  === String(xmlRow.order_item),
      );

      if (!jaExiste) {
        const assignedRecord = assignedItems.find(
          (a) => String(a.xmlItemId) === String(selectedXmlId),
        );

        const restoredPo = {
          id:          `restored-${xmlRow.purchase_order}-${xmlRow.order_item}`,
          poNumber:    xmlRow.purchase_order,
          poItem:      String(xmlRow.order_item),
          category:    assignedRecord?.category   ?? "",
          erpMaterial: xmlRow.material_sap        ?? assignedRecord?.erpMaterial ?? "",
          shortText:   xmlRow.description_material_sap ?? assignedRecord?.shortTextErp ?? "",
          qty:         xmlRow.qty                 ?? "",
          uom:         xmlRow.uom_erp             ?? "",
          iva:         xmlRow.tax_code            ?? "",
          ncm:         xmlRow.ncm_sap             ?? "",
        };

        setPoItems((prev) => [restoredPo, ...prev]);
        setAllPoItems((prev) => [restoredPo, ...prev]);
      }
    }

    setAssignedItems((prev) =>
      prev.filter((x) => String(x.xmlItemId) !== String(selectedXmlId)),
    );

    setXmlItems((prev) =>
      prev.map((row) =>
        String(row.id) === String(selectedXmlId)
          ? {
              ...row,
              purchase_order:           "",
              order_item:               "",
              tax_code:                 "",
              material_sap:             "",
              description_material_sap: "",
              uom_sap:                  "",
              ncm_sap:                  "",
              assigned:                 false,
              uomOptions:               [],
              uom_erp:                  "",
            }
          : row,
      ),
    );

    addMessage(
      "S",
      t("message.msg0011", {
        item: xmlRow.item_nf,
      }),
    );
  }

  function handleApplyConversion(conversion) {
    addMessage(
      "S",
      t("message.msg0012", {
        fromUom: conversion.fromUom,
        toUom: conversion.toUom,
        factor: conversion.factor,
      }),
    );
  }

  async function handleSimulate() {
    if (isSimulating) return;
    setIsSimulating(true);
    try {
      const result = await simulateInvoice(accessKey);
      const msgs = result.messages ?? [];
      if (msgs.length === 0) {
        addMessage("S", t("assignment.simNoMessages"));
      } else {
        msgs.forEach((m) => addMessage(m.TYPE === "E" || m.TYPE === "A" ? "E" : m.TYPE === "W" ? "W" : "S", m.MESSAGE));
      }
    } catch (err) {
      addMessage("E", t("assignment.simError", { msg: err.message }));
    } finally {
      setIsSimulating(false);
    }
  }

  function buildPayload() {
    const itemsPayload = xmlItems.map((i) => ({
      item_nf:                  i.item_nf,
      purchase_order:           i.purchase_order,
      order_item:               i.order_item,
      tax_code:                 i.tax_code,
      material_sap:             i.material_sap             ?? null,
      description_material_sap: i.description_material_sap ?? null,
      uom_sap:                  i.uom_erp                  ?? null,
      ncm_sap:                  i.ncm_sap                  ?? null,
      adm_lote:                 i.adm_lote                 ?? "",
      lote:                     i.lote                     ?? "",
    }));

    const splitLotes = xmlItems
      .filter((i) => i.purchase_order)
      .map((i) => ({
        item_nf:   i.item_nf,
        subitem:   1,
        quantity:  i.qty ?? 0,
        batch:     i.lote     ?? null,
        warehouse: i.deposito        ?? null,
        plant:     headerInfo?.plant ?? null,
        adm_lote:  i.adm_lote !== undefined
                     ? String(i.adm_lote).trim().toUpperCase() === "X"
                     : null,
      }));

    return { itemsPayload, splitLotes };
  }

  async function handleSaveOnly() {
    if (isSavingOnly) return;

    setMessages([]);
    setIsSavingOnly(true);
    try {
      const { itemsPayload, splitLotes } = buildPayload();
      await saveAssignment(accessKey, itemsPayload, splitLotes, "save");
      addMessage("S", t("message.msg0008"));
    } catch (err) {
      addMessage("E", err.message);
    } finally {
      setIsSavingOnly(false);
    }
  }

  async function handleProcess() {
    if (isSaving) return;

    const hasAssigned = xmlItems.some((i) => i.purchase_order && i.order_item);

    setMessages([]);
    setIsSaving(true);
    try {
      const { itemsPayload, splitLotes } = buildPayload();

      const result = await saveAssignment(accessKey, itemsPayload, splitLotes, "process");

      const msgs = result.messages ?? [];
      if (msgs.length === 0) {
        addMessage("S", hasAssigned ? t("assignment.saveSuccess") : t("assignment.unassignSuccess"));
      } else {
        msgs.forEach((m) => {
          const type = ["E", "A"].includes(String(m.TYPE).toUpperCase()) ? "E"
                     : String(m.TYPE).toUpperCase() === "W" ? "W"
                     : "S";
          const skip = ["", "0", "000", "000000"];
          const msgIdCode = String(m.ID     ?? "").trim();
          const msgNumber  = String(m.NUMBER ?? "").trim();
          addMessage(
            type,
            m.MESSAGE,
            skip.includes(msgIdCode) ? "" : msgIdCode,
            skip.includes(msgNumber)  ? "" : msgNumber,
          );
        });
      }

      if (result.success) {
        addMessage("S", t("assignment.saveSuccessContinuing"));

        const alreadyFinished = (result.stepsExecuted || []).some(s => s.status === "finished");
        if (alreadyFinished) setIsReadOnly(true);

        try {
          const continueResult = await continueProcess(accessKey, { selected, docType: location.state?.docType });
          if (continueResult?.finished) setIsReadOnly(true);
        } catch (_) {
          // erro no continuar processo não impede o retorno à tela principal
        }

        setTimeout(() => navigate("/inbound", { replace: true }), 1500);
      }
    } catch (err) {
      addMessage("E", err.message);
    } finally {
      setIsSaving(false);
    }
  }

  const conversionRows = React.useMemo(() => {
    return xmlItems.map((item) => {
      const assignment = assignedItems.find(
        (a) => String(a.xmlItemId) === String(item.id),
      );

      return {
        ...item,
        erpMaterial: assignment?.erpMaterial ?? "",
        shortTextErp: assignment?.shortTextErp ?? "",
        convertedUom: item.convertedUom ?? "",
      };
    });
  }, [xmlItems, assignedItems]);

  if (!selected || !headerInfo) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* TÍTULO DA PÁGINA */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <IconButton
          sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
          size="small"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}
        >
          {t("assing.title")} -{" "}
          <Typography
            component="span"
            variant="h5"
            sx={{ color: "primary.main", fontFamily: "monospace", fontWeight: 700 }}
          >
            {accessKey}
          </Typography>
        </Typography>
      </Box>

      {/* CABEÇALHO DO DOCUMENTO */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          p: 2,
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <AssignmentHeaderInfo headerInfo={headerInfo} />
      </Paper>

      {/* BARRA DE FERRAMENTAS (Ações e Pesquisa) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        {!isReadOnly && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              gap: 0.5,
              p: 0.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px",
              bgcolor: "background.paper",
            }}
          >
            <Tooltip title={t("buttons.assign")}>
              <IconButton size="small" onClick={handleAssign} sx={{ color: "#10b981" }}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("buttons.unassign")}>
              <IconButton size="small" onClick={handleUnassign} sx={{ color: "#ef4444" }}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title={t("buttons.bSave")}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleSaveOnly}
                  disabled={isSavingOnly}
                  sx={{ color: "primary.main" }}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("buttons.simulate")}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleSimulate}
                  disabled={isSimulating}
                  sx={{ color: "text.secondary" }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Paper>
        )}

        {!isReadOnly && (
          <Box sx={{ width: 320, maxWidth: "100%" }}>
            <PurchaseOrderSearch
              values={poSearch}
              onChange={setPoSearch}
              onSearch={handleSearchPo}
              loading={loadingPo}
            />
          </Box>
        )}
      </Box>

      {/* PAINÉIS LADO A LADO (Itens NF-e e Itens Pedido) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isReadOnly ? "1fr" : { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          mb: 2,
          width: "100%",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            overflow: "hidden",
            height: 380,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.subtle", flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary" }}>
              {t("assing.pendingItems")}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <XmlItemsGrid
              rows={xmlItems}
              selectedId={selectedXmlId}
              onSelect={setSelectedXmlId}
              onRowsChange={setXmlItems}
              readOnly={isReadOnly}
            />
          </Box>
        </Paper>

        {!isReadOnly && (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
              overflow: "hidden",
              height: 380,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.subtle", flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary" }}>
                {t("assing.pendingOrder")}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <PurchaseOrderGrid
                rows={poItems}
                selectedId={selectedPoId}
                onSelect={setSelectedPoId}
              />
            </Box>
          </Paper>
        )}
      </Box>

      {/* PAINEL DE MENSAGENS */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          maxHeight: 180,
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <MessagesPanel messages={messages} />
      </Paper>

      {/* RODAPÉ */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          position: "sticky",
          bottom: 0,
          py: 1,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          zIndex: 10,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate("/inbound", { replace: true })}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            px: 4,
            textTransform: "none",
            transition: "background-color 0.2s ease",
            "&:hover": {
              bgcolor: "action.hover",
              borderColor: "divider",
            },
          }}
        >
          {t("buttons.bClose")}
        </Button>
        {!isReadOnly && (
          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={isSaving}
            sx={{ px: 4, textTransform: "none" }}
          >
            {isSaving ? t("status.loading") + "..." : t("buttons.bProcess")}
          </Button>
        )}
      </Box>

      <UnitConversionDialog
        open={openUnitDialog}
        onClose={() => setOpenUnitDialog(false)}
        onApply={handleApplyConversion}
        rows={conversionRows}
      />
    </Box>
  );
};

export default AssignmentPage;